import { getMenu, type Category } from '@koala/sdk/v4';
import { type Query, onlineManager, useQuery, useQueryClient } from '@tanstack/react-query';
import { DELIVERY_TIME_WANTED_MODES } from '@/constants/global';
import { useSelector } from '@/redux';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';
import { createHttpClient } from '@/services/client';
import { __DANGEROUS_EMPTY_REQUEST_ORIGIN, getOrigin } from '@/utils';
import { toResolvedLocationId } from '@/utils/locations';
import { isRetryableError } from '@/utils/networkErrors';
import { guardedReadMeta } from '@/utils/queryCacheReporting';
import { queryRetryDelay, transportAwareRetry } from '@/utils/requests';
import { withJsonGuard } from '@/utils/withJsonGuard';

/** A focus refetch waits this long after the last failed cycle, so tab-switching during an outage
 * can't start a new retry cycle (and Sentry report) on every switch. */
const MENU_FOCUS_REFETCH_COOLDOWN_MS = 5_000;

/**
 * A menu read that failed transiently (dropped connection, 5xx) is fetched again — with the same
 * retry policy — when the tab becomes visible or the device comes back online while the query is
 * still in that error, instead of leaving the diner on the failure screen. (TEAM-15682)
 * - A 4xx is not re-sent: the same request would be refused again.
 * - Not while offline: the fetch would only pause, and a never-loaded query swaps its error panel
 *   for a spinner until the device reconnects — which `refetchOnReconnect` covers anyway.
 * - `'always'` because an errored refetch keeps the previous data's timestamp, so a stale check
 *   alone could skip it.
 * A menu that loaded is left alone (focus) or follows React-Query's default stale check
 * (reconnect). React-Query already pauses retries while the tab is hidden.
 */
const refetchIfFailed =
  (otherwise: boolean, cooldownMs = 0) =>
  ({ state }: Pick<Query, 'state'>): boolean | 'always' => {
    if (state.status !== 'error' || !isRetryableError(state.error)) return otherwise;
    if (!onlineManager.isOnline()) return false;
    return Date.now() - state.errorUpdatedAt >= cooldownMs ? 'always' : false;
  };

const ASAP_STALE_TIME_MS = 120000;
const SCHEDULED_STALE_TIME_MS = 300000;

interface MenuService {
  /**
   * Nullable on purpose: redux hands this hook `null` (`basket.content.location.id`)
   * and `undefined` (`locations.detail`) long before a location resolves. Typing it
   * honestly is what lets `toResolvedLocationId` screen both. (TEAM-13663)
   */
  id: number | null | undefined;
  ifModifiedSince?: string;
  wantedAt?: string;
}

function resolveRequestOrigin(): string {
  return typeof window !== 'undefined'
    ? getOrigin(window.location.host)
    : __DANGEROUS_EMPTY_REQUEST_ORIGIN;
}

/**
 * The single source of truth for the menu cache key. `useMenu`, `usePrefetchMenu`
 * and the store page's SSR batch (`pages/store/[...id].tsx`) all build it from
 * here, so a warmed or dehydrated entry is always the one the store page reads
 * back — the keys cannot drift apart. (TEAM-13663)
 */
export function getMenuQueryKey({ id, ifModifiedSince, wantedAt }: MenuService) {
  return [
    'menu',
    {
      // Normalized, so a drifted `"19543"` and a well-formed `19543` share one
      // entry instead of fragmenting the cache — and so every unresolved sentinel
      // collapses to one disabled key rather than three.
      id: toResolvedLocationId(id),
      ifModifiedSince,
      wantedAt: wantedAt ?? DELIVERY_TIME_WANTED_MODES.ASAP,
    },
  ];
}

/**
 * Whether any category of a menu has a product to order. The store page shows the closed /
 * unavailable message when none does, and its server render tells the edge cache not to keep
 * such a page (`pages/store/[...id].tsx`), so both read this one rule. (TEAM-16180)
 */
export function hasAvailableProducts(categories: Pick<Category, 'products'>[]) {
  return categories.some((category) => category.products.length > 0);
}

/**
 * The menu read plus the query options that must travel with it — shared by `useMenu` and
 * `usePrefetchMenu`, so the two cannot drift apart:
 * - the guard runs with `report: false` and `meta` hands the report to the query cache, once,
 *   after the last retry; built together here because one without the other would silently drop
 *   the Sentry event;
 * - a store page that mounts while the prefetch is in flight joins that fetch, so both must retry
 *   the same way: 2 retries (300 ms, 600 ms) for a server error, the longer transport budget for a
 *   dropped connection. (TEAM-15682)
 */
function menuQuery(
  rawId: MenuService['id'],
  { ifModifiedSince, wantedAt }: Omit<MenuService, 'id'>,
  client: ReturnType<typeof createHttpClient>,
) {
  const id = toResolvedLocationId(rawId);
  const reportContext =
    id === undefined
      ? undefined
      : { route: '/store/[...id]', urlHint: '/menus', locationId: id.toString() };

  return {
    queryFn: () => {
      /**
       * `enabled` already screens this in `useMenu`, but `refetch()` does not consult
       * `enabled` in react-query v4 — the store page's "Try again" button
       * (`features/store/details.tsx`) re-runs this function whatever the gate
       * says. Failing here keeps an unresolved id off the wire instead of
       * requesting `store-locations/0/menu` for a guaranteed 404. (TEAM-13663)
       */
      if (id === undefined || reportContext === undefined) {
        throw new Error(`useMenu requires a resolved location id, received ${String(rawId)}`);
      }

      return withJsonGuard(
        () =>
          getMenu(
            {
              id,
              ifModifiedSince,
              wantedAt: wantedAt === DELIVERY_TIME_WANTED_MODES.ASAP ? undefined : wantedAt,
              compressed: true,
            },
            { client },
          ),
        reportContext,
        { report: false },
      );
    },
    meta: reportContext && guardedReadMeta(reportContext),
    retry: transportAwareRetry(2),
    retryDelay: queryRetryDelay,
  };
}

function menuStaleTime(wantedAt?: string) {
  return wantedAt === DELIVERY_TIME_WANTED_MODES.ASAP
    ? ASAP_STALE_TIME_MS
    : SCHEDULED_STALE_TIME_MS;
}

export function useMenu({ id, ifModifiedSince, wantedAt }: MenuService) {
  const client = createHttpClient({ origin: resolveRequestOrigin() });
  const resolvedId = toResolvedLocationId(id);
  const { data, status, fetchStatus, refetch, error } = useQuery({
    queryKey: getMenuQueryKey({ id, ifModifiedSince, wantedAt }),
    ...menuQuery(id, { ifModifiedSince, wantedAt }, client),
    /**
     * Only fire once the location id has resolved. The previous gate
     * (`id !== undefined && id !== 0`) let `null` through — `null` is neither —
     * and the `id ?? 0` fallback then coerced it to `0`, which is how
     * `store-locations/0/menu` kept 404ing after TEAM-14044/14239. (TEAM-13663)
     */
    enabled: resolvedId !== undefined,
    refetchOnWindowFocus: refetchIfFailed(false, MENU_FOCUS_REFETCH_COOLDOWN_MS),
    refetchOnReconnect: refetchIfFailed(true),
    staleTime: menuStaleTime(wantedAt),
    keepPreviousData: true,
  });

  return {
    categories: data?.[0] ? data[0] : [],
    status,
    isLoading: fetchStatus === 'fetching',
    refetch,
    // Surfaced so the store page can tell a rejected wanted-at apart from a genuine
    // failure and recover instead of offering a retry that cannot succeed (TEAM-14594).
    error,
  };
}

/**
 * Warms the menu cache for a location ahead of navigation — the locator calls it
 * on card hover/focus/select — so the request overlaps the route transition to
 * `/store/[...id]` instead of starting after the store page mounts.
 *
 * It writes the key `useMenu` reads, and takes `wantedAt` from the same
 * `selectConveyance` slice the store page does, so a diner holding a scheduled
 * time warms the entry that page will actually ask for rather than an ASAP one it
 * would ignore. react-query de-dupes in-flight fetches and honours `staleTime`,
 * so repeated hovers cost at most one request per location. (TEAM-13663)
 */
export function usePrefetchMenu() {
  const queryClient = useQueryClient();
  // Only the primitive, not the whole conveyance slice: the locator renders one
  // card per location, and subscribing each to an object would re-render the
  // entire list on any basket change.
  const wantedAt = useSelector((state) => selectConveyance(state).time_wanted);

  return (id: number | null | undefined) => {
    // Mirror the useMenu gate: never warm the cache under an unresolved id.
    const resolvedId = toResolvedLocationId(id);

    if (resolvedId === undefined) {
      return;
    }

    const queryKey = getMenuQueryKey({ id: resolvedId, wantedAt });

    // A failed entry is left for the store page, which refetches it on mount: an errored entry
    // is always stale, so every hover would otherwise start a new full retry cycle. (TEAM-15682)
    const cached = queryClient.getQueryState(queryKey);
    if (cached?.status === 'error' && cached.fetchStatus === 'idle') {
      return;
    }

    const client = createHttpClient({ origin: resolveRequestOrigin() });
    const { queryFn, ...options } = menuQuery(resolvedId, { wantedAt }, client);

    void queryClient.prefetchQuery(queryKey, queryFn, {
      staleTime: menuStaleTime(wantedAt),
      ...options,
    });
  };
}
